/**
 * Settings (screenshots/51, brief §2.9; the handoff starter's settings/Settings.tsx, built out): Notifications, Your public record ("This is me" claim + hide my
 * estimate), Account, Sidebar, Privacy & data, API keys (ApiKeys.tsx), and Team follows for team owners/admins (#team-follows, linked
 * from "Manage team follows"). Paints from snapshot:account; every switch saves at once (PUT /api/dashboard/settings).
 * Notifications only fire when something changed: that's the producer's rule, stated in the card's subtitle.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { ArrowRight, UserCheck, X } from "lucide-react";
import { Switch } from "../components/shared/ui";
import { TypeBadge } from "../components/following/TypeBadge";
import { GROUPS } from "../nav/navConfig";
import { navStore } from "../nav/navStore";
import { followsStore, useFollows } from "../stores/followsStore";
import { toastStore } from "../stores/toastStore";
import type { AccountSettings, AccountSnapshot, NotifySettings } from "../account/types";
import { navigate } from "../app/router";
import { prefersReducedMotion } from "../motion";
import { Card } from "./Card";
import { ListRow, RowMain, RowSub, RowTitle, RowTrail } from "../components/shared/List";
import { ApiKeys } from "./ApiKeys";
import { AppearanceCards, CustomThemeEditor } from "./Appearance";

type SectionId = "account" | "notifications" | "appearance" | "public-record" | "sidebar" | "privacy" | "api-keys" | "team-follows";
const fmtDay = (iso: string) => new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });
const lastName = (name: string) => name.split(/\s+/).slice(1).join(" ") || name;

const put = (path: string, body: unknown) =>
  fetch(path, { method: "PUT", credentials: "include", headers: { "content-type": "application/json" }, body: JSON.stringify(body) }).then((r) => r.ok).catch(() => false);


function ToggleRow({ title, text, on, onChange }: { title: string; text?: string; on: boolean; onChange: (v: boolean) => void }) {
  return (
    <ListRow as="div" className="set-row">
      <RowMain className="set-row__text"><RowTitle as="b">{title}</RowTitle>{text && <RowSub wrap>{text}</RowSub>}</RowMain>
      <RowTrail><Switch on={on} onChange={onChange} label={title} /></RowTrail>
    </ListRow>
  );
}

function KvRow({ label, value, action, href, onClick }: { label: string; value: ReactNode; action: string; href?: string; onClick?: () => void }) {
  return (
    <div className="set-kv">
      <span className="set-kv__label">{label}</span>
      <span className="set-kv__value">{value}</span>
      {href ? <a className="link-arrow" href={href}>{action}<ArrowRight size={14} /></a>
        : <button className="link-arrow" onClick={onClick}>{action}<ArrowRight size={14} /></button>}
    </div>
  );
}

export function Settings({ account, canManageTeam }: { account: AccountSnapshot; canManageTeam: boolean }) {
  const [settings, setSettings] = useState<AccountSettings>(account.settings);
  const [wardTotals, setWardTotals] = useState(account.wardTotals);
  const [claim, setClaim] = useState(account.claim);
  const resident = account.group === "resident";
  const sections: { id: SectionId; label: string }[] = [
    { id: "account", label: "Account" },
    { id: "notifications", label: "Notifications" },
    { id: "appearance", label: "Appearance" },
    ...(resident ? [{ id: "public-record" as const, label: "Your public record" }] : []),
    { id: "sidebar", label: "Sidebar" },
    ...(resident ? [{ id: "privacy" as const, label: "Privacy & data" }] : []),
    { id: "api-keys", label: "API keys" },
    ...(canManageTeam ? [{ id: "team-follows" as const, label: "Team follows" }] : []),
  ];
  const [active, setActive] = useState<SectionId>(() => (location.hash.slice(1) as SectionId) || "notifications");
  const root = useRef<HTMLDivElement>(null);
  // Settings → Appearance → "Create a custom theme" opens the editor in place of the cards (design vcwY7).
  const [editingTheme, setEditingTheme] = useState(false);
  useEffect(() => { document.title = "Settings · Chicago Politics"; }, []);

  // Deep links (#team-follows, #notifications) and the section nav: scroll-spy on the cards.
  useEffect(() => {
    const id = location.hash.slice(1);
    if (id) document.getElementById(id)?.scrollIntoView({ block: "start" });
    const cards = [...(root.current?.querySelectorAll<HTMLElement>(".set-card") ?? [])];
    const io = new IntersectionObserver((entries) => {
      const top = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
      if (top) setActive(((top.target as HTMLElement).dataset.section || top.target.id) as SectionId);
    }, { rootMargin: "0px 0px -60% 0px" });
    cards.forEach((c) => io.observe(c));
    return () => io.disconnect();
  }, [editingTheme]);

  const save = async (patch: Partial<AccountSettings>) => {
    const prev = settings;
    setSettings({ ...settings, ...patch, notify: { ...settings.notify, ...(patch.notify ?? {}) } });
    if (!(await put("/api/dashboard/settings", patch))) { setSettings(prev); toastStore.getState().show({ message: "Couldn't save that. Try again." }); }
  };
  const notify = (k: keyof NotifySettings) => (v: boolean) => void save({ notify: { ...settings.notify, [k]: v } });
  const saveWardTotals = async (v: boolean) => {
    setWardTotals(v);
    if (!(await put("/api/dashboard/onboarding", { wardTotals: v }))) { setWardTotals(!v); toastStore.getState().show({ message: "Couldn't save that. Try again." }); }
  };
  const claimIt = async (personId: string) => {
    const res = await fetch("/api/dashboard/claims", { method: "POST", credentials: "include", headers: { "content-type": "application/json" }, body: JSON.stringify({ personId }) }).catch(() => null);
    if (res?.ok) setClaim({ personId, status: "pending" });
  };
  const jump = (id: SectionId) => {
    if (editingTheme) { setEditingTheme(false); setActive(id); history.replaceState(null, "", `#${id}`); requestAnimationFrame(() => document.getElementById(id)?.scrollIntoView({ block: "start" })); return; }
    setActive(id);
    history.replaceState(null, "", `#${id}`);
    document.getElementById(id)?.scrollIntoView({ behavior: prefersReducedMotion() ? "auto" : "smooth", block: "start" });
  };
  const candidate = account.candidates[0];

  return (
    <div className="settings-page" ref={root}>
      <h1 className="page-title">Settings</h1>
      <div className="settings-layout">
        <nav className="settings-nav" aria-label="Settings">
          {sections.map((s) => (
            <a key={s.id} href={`#${s.id}`} aria-current={active === s.id ? "true" : undefined} onClick={(e) => { e.preventDefault(); jump(s.id); }}>{s.label}</a>
          ))}
        </nav>
        <div className="settings-cards">
          {editingTheme ? <CustomThemeEditor onDone={() => { setEditingTheme(false); setActive("appearance"); requestAnimationFrame(() => document.getElementById("appearance")?.scrollIntoView({ block: "start" })); }} /> : <>
          <Card id="notifications" title="Notifications" sub="Email and in-app. Nothing is sent when nothing changed.">
            <ToggleRow title="Followed items change" text="A bill you follow moves a step, is put on an agenda, or gets a roll call" on={settings.notify.followedChanges} onChange={notify("followedChanges")} />
            {resident ? account.alderperson && (
              <ToggleRow title="Your alderperson's likely vote changes" text={`When a forecast for Ald. ${lastName(account.alderperson.name)} moves 10 points or more on something you follow`}
                on={settings.notify.forecastMoves} onChange={notify("forecastMoves")} />
            ) : (
              <ToggleRow title="A forecast moves 10 points or more" text="Only on items you follow" on={settings.notify.forecastMoves} onChange={notify("forecastMoves")} />
            )}
            <ToggleRow title="Live alerts" text="When an item you follow is called during a live City Council meeting" on={settings.notify.liveAlerts} onChange={notify("liveAlerts")} />
            <ToggleRow title="Weekly digest" text="Mondays at 7 AM, only when something matched" on={settings.digest} onChange={(v) => void save({ digest: v })} />
            <ToggleRow title="New filings that match your topics" text="After each City Council meeting" on={settings.notify.newFilings} onChange={notify("newFilings")} />
          </Card>

          <AppearanceCards onCreate={() => { setEditingTheme(true); setActive("appearance"); }} />

          {resident && (
            <Card id="public-record" title="Your public record" sub="What the dashboard shows about you, from public meeting video">
              {claim ? claim.status === "pending" && (
                <div className="set-claim">
                  <div className="set-claim__text"><b>This is me</b></div>
                  <span className="chip chip--neutral">Pending</span>
                </div>
              ) : candidate && (
                <div className="set-claim">
                  {candidate.imageUrl ? <img className="set-claim__frame" src={candidate.imageUrl} alt="" width={72} height={44} /> : <span className="set-claim__frame" />}
                  <div className="set-claim__text">
                    <b>We found {candidate.comments} public comment{candidate.comments === 1 ? "" : "s"} that may be you</b>
                    <span>{candidate.meetings.map((m) => `${m.body}, ${fmtDay(m.date)}`).join(" · ")}. Claim them to see your leaning estimate, add context, or ask us to remove them.</span>
                  </div>
                  <button className="btn btn--primary btn--lg" onClick={() => void claimIt(candidate.personId)}><UserCheck size={16} />This is me</button>
                </div>
              )}
              <ToggleRow title="Show my estimate on my profile" text="Off hides your AI leaning estimate from everyone, including you" on={settings.showEstimate} onChange={(v) => void save({ showEstimate: v })} />
            </Card>
          )}

          <Card id="account" title="Account">
            <KvRow label="Name" value={account.name} action="Edit" href="/account" />
            <KvRow label="Email" value={account.email} action="Edit" href="/account" />
            <KvRow label="User group" value={GROUPS[account.group].label} action="Change" onClick={() => navigate("/dashboard/welcome")} />
            {resident && <KvRow label="Address" value={account.ward ? `Ward ${account.ward}` : ""} action="Edit" onClick={() => navigate("/dashboard/welcome/ward")} />}
          </Card>

          <Card id="sidebar" title="Sidebar">
            <KvRow label="Edit sidebar" value="" action="Edit" onClick={() => navStore.getState().setEditing(true)} />
            <KvRow label={`Reset to ${GROUPS[account.group].label} default`} value="" action="Reset" onClick={() => navStore.getState().reset()} />
          </Card>

          {resident && (
            <Card id="privacy" title="Privacy & data">
              <ToggleRow title={`Count my "How would you vote?" answers in ${account.ward ? `Ward ${account.ward}'s` : "your ward's"} totals`} text="One person, one position. Your name is never shared." on={wardTotals} onChange={(v) => void saveWardTotals(v)} />
            </Card>
          )}

          <ApiKeys />

          {canManageTeam && <TeamFollows />}
          </>}
        </div>
      </div>
    </div>
  );
}

/** #team-follows: the team's follows, removable by owners/admins ("Manage team follows" on the Meetings page). */
function TeamFollows() {
  const team = useFollows((s) => s.follows).filter((f) => f.scope === "team");
  return (
    <Card id="team-follows" title="Team follows">
      {team.map((f) => (
        <ListRow as="div" className="set-row" key={f.id}>
          <RowMain className="set-row__text set-row__text--inline"><TypeBadge type={f.type} /><RowTitle as="b">{f.label}</RowTitle></RowMain>
          <RowTrail><button className="icon-btn" aria-label={`Unfollow ${f.label}`} onClick={() => void followsStore.getState().unfollow(f.id)}><X size={16} /></button></RowTrail>
        </ListRow>
      ))}
    </Card>
  );
}
